import React from 'react';
import { Divider, Typography } from '@mui/material';
import { EditOutlined, EmailOutlined, Delete } from '@mui/icons-material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import PageFilters, { Filter } from 'components/new/PageFilters';
import StatusBadge from 'components/new/StatusBadge';
import { Column, Row } from 'components/Layout';
import SearchField from 'further-ui/components/SearchField';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Avatar from 'further-ui/components/Avatar';
import EditUserDrawer from './EditUserDrawer';
import AddUserDrawer from './AddUserDrawer';
import { useUserManagementNavigationContext } from '../UserManagementNavigationContext';

const pageId = 'user-management';

const Users: React.FC = () => {
  const removeUserDisclosure = useDisclosure<{ id: string }>();
  const {
    editUserDrawerUserId,
    openEditUserDrawer,
    closeEditUserDrawer,
    isAddUserDrawerOpen,
    closeAddUserDrawer,
  } = useUserManagementNavigationContext();
  const { pagination, filters, handleChangeFilters } = usePaginatedFilters(
    pageId,
    {
      defaultFilters: { searchQuery: '' },
      defaultOrder: { orderBy: 'firstName', order: 'asc' },
      orderByOptions: ['firstName', 'lastName', 'email', 'createdAt'],
    },
  );

  const [users, { isFetching }] =
    api.adminUser.listCurrentFirmUsers.useSuspenseQuery({});

  const requestPasswordReset =
    api.adminUser.requestPasswordResetForUser.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Password reset email sent',
        invalidations: [],
      }),
    );

  const deleteUser = api.adminUser.removeUserFromCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User removed successfully.',
      onSuccess: () => {
        removeUserDisclosure.onClose();
      },
    }),
  );

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    if (!filters.searchQuery) return true;

    const searchLower = filters.searchQuery.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      user.roles?.some((role) => role.name.toLowerCase().includes(searchLower))
    );
  });

  const paginatedUsers = filteredUsers.slice(
    (pagination.page - 1) * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage,
  );

  const columns: Array<TableColumn<(typeof users)[number]>> = [
    {
      label: 'User',
      key: 'userNameAndEmail',
      render: (user) => (
        <Row centered spacing="xs">
          <Avatar
            text={`${user.firstName} ${user.lastName}`}
            src={user.profilePicUrl}
            size="lg"
            color="random"
          />
          <Column>
            <Typography variant="inherit">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Column>
        </Row>
      ),
    },
    {
      label: 'Role',
      key: 'role',
      render: (user) => (
        <Typography variant="inherit">{user.roles?.[0]?.name}</Typography>
      ),
    },
    {
      label: 'MFA enrolled',
      key: 'hasMfaEnrolled',
      render: (user) => (
        <StatusBadge color={user.hasMfaEnrolled ? 'success' : 'error'} />
      ),
    },
    {
      label: 'Last login',
      key: 'lastLogin',
      render: (user) => (
        <Typography variant="inherit">
          {user.lastLogin ? user.lastLogin.toReadable() : 'Never'}
        </Typography>
      ),
    },
    {
      label: 'Date added',
      key: 'createdAt',
      render: (user) => (
        <Typography variant="inherit">{user.createdAt.toReadable()}</Typography>
      ),
    },
  ];

  return (
    <Column spacing="lg">
      <PageFilters>
        <Filter size="sm">
          <SearchField
            autoSearchOnDebounce
            debounceTime={100}
            name="searchQuery"
            placeholder="Search by user"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
          />
        </Filter>
      </PageFilters>
      <Divider />
      <Table
        columns={columns}
        rows={paginatedUsers}
        isLoading={isFetching || pagination.isPaginationChanging}
        rowsTotalCount={filteredUsers.length}
        disableSorting
        pagination={pagination}
        emptyMessage="No users found"
        actions={[
          {
            label: 'Edit',
            icon: EditOutlined,
            onClick: (user) => {
              openEditUserDrawer(user.id);
            },
          },
          {
            label: 'Send password reset email',
            icon: EmailOutlined,
            onClick: (user) => {
              requestPasswordReset.mutate({ id: user.id });
            },
            disabled: requestPasswordReset.isPending,
          },
          {
            label: 'Delete user',
            icon: Delete,
            onClick: (user) => {
              removeUserDisclosure.stageAction({ id: user.id });
            },
            disabled: deleteUser.isPending,
          },
        ]}
      />
      <ConfirmDialog
        open={removeUserDisclosure.isOpen}
        onClose={removeUserDisclosure.onClose}
        title="Remove user"
        content={
          <Typography variant="body2">
            This will remove the user from your firm. They will no longer have
            access to your firm's data.
          </Typography>
        }
        loading={deleteUser.isPending}
        actions={{
          confirm: {
            label: 'Remove',
            color: 'error',
            onClick: () => {
              deleteUser.mutate({
                adminUserId: removeUserDisclosure.actionProps.id,
              });
            },
          },
        }}
      />
      <EditUserDrawer
        open={!!editUserDrawerUserId}
        onClose={closeEditUserDrawer}
        userId={editUserDrawerUserId}
      />
      <AddUserDrawer
        open={isAddUserDrawerOpen}
        onClose={closeAddUserDrawer}
        onUserCreated={(userId) => {
          closeAddUserDrawer();
          openEditUserDrawer(userId);
        }}
      />
    </Column>
  );
};

export default Users;
